import { createElement, forwardRef, ComponentPropsWithoutRef, CSSProperties } from 'react';
import { useDesignTokens, DesignTokens, renderInlineCssVars } from '../../../theme';

import { useIsRTL } from '../../../locale';
import { postfixTestId, TestIdProps } from '../../../util/testId';
import { InvisibleText } from '../../InvisibleText';
import cx from 'classnames';
import cn from './SliderRange.css.json';
import './SliderRange.css';

export interface SliderRangeStyle {
  minPos: number;
  maxPos: number;
  orientation: 'horizontal' | 'vertical';
  enableFocusOutline?: boolean;
}

export type SliderRangeProps = {
  xStyle: SliderRangeStyle;
  ariaLabel?: string;
  disabled?: boolean;
} & ComponentPropsWithoutRef<'div'> &
  TestIdProps;

export const SliderRange = forwardRef<HTMLDivElement, SliderRangeProps>(function SliderRange(
  { xStyle, className, style: userStyle, ...props },
  ref
) {
  const style: CSSProperties = {};

  const isRTL = useIsRTL();

  const length = `${(xStyle.maxPos - xStyle.minPos) * 100}%`;
  if (xStyle.orientation === 'vertical') {
    style.top = `${(1 - xStyle.maxPos) * 100}%`;
    style.height = length;
  } else {
    style.left = `${(isRTL ? 1 - xStyle.maxPos : xStyle.minPos) * 100}%`;
    style.width = length;
  }

  const dt = useDesignTokens();

  const tokens = renderInlineCssVars(getSliderRangeToken(dt), 'slider-range');

  const { testId, ariaLabel, ...rest } = props;
  const invisibleTextId = props['aria-labelledby'];

  return (
    <div
      data-testid={postfixTestId(props.testId, 'container')}
      className={cn.container[xStyle.orientation]}
    >
      {ariaLabel ? (
        <InvisibleText id={invisibleTextId!} testId={postfixTestId(props.testId, 'invisibleText')}>
          {ariaLabel!}
        </InvisibleText>
      ) : undefined}
      <div
        {...rest}
        data-testid={props.testId}
        ref={ref}
        className={cx(
          className,
          props.disabled && cn.disabled,
          cn[xStyle.orientation],
          xStyle.enableFocusOutline && cn.rangeEnableFocusOutline
        )}
        style={{ ...tokens, ...style, ...userStyle }}
      >
        {props.children}
      </div>
    </div>
  );
});

const getSliderRangeToken = (dt: DesignTokens) => {
  if (dt.enableNovaTokens) {
    return {
      bg: dt.stateSecondaryToggleNova.selected.bg,
      'disabled-bg': dt.stateSecondaryToggleNova.disabledSelected.bg,
    };
  } else {
    return {
      bg: dt.stateSecondaryToggle.selected.bg,
    };
  }
};
